import { FC, useEffect, useMemo, useState } from 'react'
import { useLazyQuery } from '@apollo/client'
import { Tab } from 'react-bootstrap'

import { useAppDispatch, useAppSelector } from 'hooks/store'
import useLoadingDelay from 'hooks/useLoadingDelay'
import GoBackHeader from 'components/organisms/GoBackHeader'
import {
  GET_COURSE_DASHBOARD,
  GetCourseDashboardResponse
} from 'share/queries/courses'
import {
  makeCourseActive,
  setInviteData,
  setOrderData,
  setTabFirst,
  setTabSecond,
  loadCourseDashboardFromResponse
} from 'reducers/activecourse/activecourseSlice'

import CourseTabMain from 'components/features/dashboard/course/tab/CourseTabMain'
import TemplateView from 'components/organisms/TemplateView'
import StyledTabs from 'components/molecules/StyledTabs'
import { loadingSpinnerPortal } from 'components/atoms/LoadingSpinner'
import ErrorMessage from 'components/atoms/ErrorMessage'

import { TooltipText } from './styles'

const errorMsg =
  'Course could not be started. Please try again or contact support at 888.410.6988.'

const firstTooltipText =
  "To complete your bankruptcy you're required to take two financial education courses. The first course is a credit counseling course that you must take before your case can be filed.\n\nAfter your case is filed you'll be required to take the second course, a financial management course."

const secondTooltipText =
  'You are required to take two financial education courses. First, you must complete a credit counseling course before your attorney can file your case. \n\nSecond, after your attorney files your case, you must complete a debtor education course.'

const Courses: FC = () => {
  const dispatch = useAppDispatch()
  const activeCase = useAppSelector((state) => state.activeCase)
  const activeCourse = useAppSelector((state) => state.activeCourse)
  const [key, setKey] = useState(() =>
    activeCourse.activeTab === 'first' ? 'first-course' : 'second-course'
  )

  const [getCourseDashboard, { loading, data, error }] =
    useLazyQuery<GetCourseDashboardResponse>(GET_COURSE_DASHBOARD, {
      fetchPolicy: 'network-only'
    })

  useEffect(() => {
    if (
      activeCase &&
      activeCase.matter &&
      activeCase.matter.id &&
      !activeCase.matter.hasLegacyOrder
    ) {
      getCourseDashboard({ variables: { caseId: activeCase.matter.id } })
    }
  }, [activeCase])

  useEffect(() => {
    if (data) {
      const courseResult = loadCourseDashboardFromResponse(data)
      dispatch(setInviteData(courseResult.invites))
      dispatch(setOrderData(courseResult.orders))
      dispatch(makeCourseActive())
    }
  }, [data])

  const tooltip = useMemo(
    () =>
      !activeCase?.matter?.hasLegacyOrder && (
        <TooltipText>
          {activeCourse.activeTab === 'first'
            ? firstTooltipText
            : secondTooltipText}
        </TooltipText>
      ),
    [activeCase?.matter?.hasLegacyOrder, activeCourse.activeTab]
  )

  const isLegacyOrder = activeCase?.matter?.hasLegacyOrder

  const firstLoadings = !activeCourse?.isActiveCourse && loading

  const secondLoadings = activeCourse.isLoadingCourse || loading

  const firstLoadingDelay = useLoadingDelay(firstLoadings)
  const secondLoadingDelay = useLoadingDelay(secondLoadings)

  if (firstLoadingDelay) return loadingSpinnerPortal()

  if (error) {
    return <div>Error ...</div>
  }

  if (!activeCourse) {
    return <div></div>
  }

  if (!activeCourse.isActiveCourse && !isLegacyOrder) {
    return <div></div>
  }

  function handleSelect(tabKey: string | null) {
    if (tabKey === key) return

    setKey(tabKey ? tabKey : 'first')

    if (key === 'first-course') {
      dispatch(setTabSecond())
    } else {
      dispatch(setTabFirst())
    }
  }

  return (
    <>
      {secondLoadingDelay && loadingSpinnerPortal()}
      <GoBackHeader text='Courses' />
      {activeCourse.errorMessage && (
        <ErrorMessage>
          <span data-testid='coures-error-message'>{errorMsg}</span>
        </ErrorMessage>
      )}

      <TemplateView title={activeCourse.title} tooltip={tooltip}>
        <StyledTabs
          defaultActiveKey='first-course'
          activeKey={key}
          id='ui-docs-tabs mb-0'
          onSelect={handleSelect}
        >
          <Tab
            eventKey='first-course'
            title='First Course'
            data-testid='courses-first-tab-content'
          >
            {activeCourse.activeTab === 'first' && <CourseTabMain />}
          </Tab>
          <Tab
            eventKey='second-course'
            title='Second Course'
            data-testid='courses-second-tab-content'
          >
            {activeCourse.activeTab === 'second' && <CourseTabMain />}
          </Tab>
        </StyledTabs>
      </TemplateView>
    </>
  )
}

export default Courses
